import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderPlus, Search, FileText, CalendarDays, GitMerge } from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import type { PatientResume } from '../lib/types'
import { Carte, Chargement, EtatVide, Message, Pastille } from '../composants/base'
import * as f from '../lib/format'
import { MODES_DOSSIER, TYPES_VENUE } from '@doc/shared'
import { styleMode } from '../lib/couleurs'

export default function Patients() {
  const naviguer = useNavigate()
  const client = useQueryClient()
  const [saisie, setSaisie] = useState('')
  const [terme, setTerme] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    const minuteur = setTimeout(() => setTerme(saisie.trim()), 220)
    return () => clearTimeout(minuteur)
  }, [saisie])

  const { data, isLoading } = useQuery({
    queryKey: ['patients', terme],
    queryFn: () =>
      api<{ patients: PatientResume[] }>(
        `/api/patients?limite=100${terme ? `&q=${encodeURIComponent(terme)}` : ''}`,
      ),
  })

  const nouveau = useMutation({
    mutationFn: () =>
      api<{ patient: PatientResume }>('/api/patients', { methode: 'POST', corps: {} }),
    onSuccess: ({ patient }) => {
      client.invalidateQueries({ queryKey: ['patients'] })
      naviguer(`/patients/${patient.id}?identite=1`)
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <div className="mx-auto max-w-[1180px] pt-1">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h1 className="text-[20px] font-semibold tracking-tight">Patients</h1>
        <button
          className="bouton-primaire"
          onClick={() => nouveau.mutate()}
          disabled={nouveau.isPending}
        >
          <FolderPlus className="h-4 w-4" />
          Nouveau patient
        </button>
      </div>

      {erreur && (
        <div className="mb-4">
          <Message ton="critique">{erreur}</Message>
        </div>
      )}

      <div className="relative mb-4 max-w-[460px]">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-brume" />
        <input
          className="champ pl-9"
          placeholder="Nom, nom de naissance, prénom ou référence"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
      </div>

      <Carte>
        {isLoading ? (
          <Chargement />
        ) : !data || data.patients.length === 0 ? (
          <EtatVide
            texte={
              terme
                ? 'Aucun dossier ne correspond à cette recherche.'
                : 'Aucun dossier pour l’instant. Un dossier peut être créé sans aucune identité : la référence suffit.'
            }
          />
        ) : (
          <ul className="px-2 py-2">
            {data.patients.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/patients/${p.id}`}
                  className="flex items-center gap-4 rounded-lg px-3 py-3 hover:bg-bleu-50"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-[14.5px] font-medium">{p.affichage}</span>
                      {p.fusionne && (
                        <Pastille icone={<GitMerge className="h-3 w-3" />}>
                          Fusionné dans un autre dossier
                        </Pastille>
                      )}
                    </span>
                    <span className="mt-0.5 block truncate text-[12.5px] text-brume">
                      {p.reference}
                      {p.dateNaissance
                        ? ` · née le ${f.dateCourte(p.dateNaissance)} (${f.age(p.dateNaissance)})`
                        : ' · date de naissance non renseignée'}
                    </span>
                  </span>

                  <span className="hidden w-[170px] shrink-0 sm:block">
                    {p.modeAffichage && (
                      <span className="pastille" style={styleMode(p.modeAffichage)}>
                        {MODES_DOSSIER[p.modeAffichage]}
                      </span>
                    )}
                  </span>

                  <span className="hidden w-[150px] shrink-0 text-[12.5px] text-brume md:block">
                    {p.derniereVenue ? (
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {TYPES_VENUE[p.derniereVenue.type as keyof typeof TYPES_VENUE]}{' '}
                        {f.dateCourte(p.derniereVenue.date)}
                      </span>
                    ) : (
                      'Aucune venue'
                    )}
                  </span>

                  <span className="hidden w-[90px] shrink-0 items-center gap-1.5 text-[12.5px] text-brume lg:flex">
                    <FileText className="h-3.5 w-3.5" />
                    {p.nbDocuments ?? 0}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Carte>
    </div>
  )
}
